import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import * as R from 'ramda'
import { lifecycle } from 'recompose'

import Memory from 'components/form/memory/index'
import withSagas from './sagas'
import { createNamedReducer } from 'helpers/ReducerUtil'
import { createNamedAction } from 'helpers/ActionUtil'
import { memoryValueReducer, createUiReducer } from './reducer'
import injectReducer from 'helpers/injectReducer'
import getSelectors from './selectors'
import {
  updateClockValues,
  togglePopup,
  getUiActions,
  updateConfigSelection,
  updateConfigSource,
  addConfig,
  deletePart,
  updateCurConfig,
  initConfig,
  runRiskValidation as runRiskValidationAction
} from './actions'
import {
  getPath,
  combinePath,
  getUiPath
} from 'containers/Form/util'

const withStateReducer = (path, id) => {
  const valuePath = getPath(path, id)
  const valueReducer = createNamedReducer(valuePath, memoryValueReducer)
  return injectReducer(valuePath, valueReducer)
}

const withUiReducer = (path, id) => {
  const uiPath = getUiPath(id)
  const uiReducer = createUiReducer(uiPath)
  return injectReducer(uiPath, uiReducer)
}

const withConnect = (path, id) => {
  const combinedPath = combinePath(path, id)
  const valuePath = getPath(path, id)
  const uiPath = getUiPath(id)
  const updateClockAction = createNamedAction(valuePath, updateClockValues)
  const initConfigAction = createNamedAction(valuePath, initConfig)
  const addConfigAction = createNamedAction(valuePath, addConfig)
  const deletePartAction = createNamedAction(valuePath, deletePart)

  const togglePopupAction = createNamedAction(uiPath, togglePopup)
  const updateConfigAction = createNamedAction(uiPath, updateConfigSelection)
  const updateConfigSourceAction = createNamedAction(uiPath, updateConfigSource)
  const updateCurConfigAction = createNamedAction(uiPath, updateCurConfig)

  const {
    risksSelector,
    configSelectionSelector,
    configSourceSelector,
    togglePopupSelector,
    partTypeSelector,
    densitySelector,
    orgSizeSelector,
    orgWidthSelector,
    channelSelector,
    configSelector,
    filteredSourcesSelector,
    approvedSourcesSelector,
    riskSourcesSelector,
    filterPairsSelector,
    approvedFilterPairsSelector,
    riskFilterPairsSelector,
    memoryConfigsSelector,
    curConfigSelector,
    isAddPartSelector,
  } = getSelectors(combinedPath, uiPath)

  const {
    togglePartType,
    toggleDensity,
    toggleOrgSize,
    toggleOrgWidth,
    toggleChannel,
    toggleConfig,
    initFilters,
    initConfigFilter,
    refreshFilters,
    isAddPart,
  } = getUiActions(uiPath)

  const mapStateToProps = state => {
    return {
      sources: filteredSourcesSelector(state),
      approvedSources: approvedSourcesSelector(state),
      riskSources: riskSourcesSelector(state),
      values: memoryConfigsSelector(state),
      configSelection: configSelectionSelector(state),
      configSource: configSourceSelector(state),
      togglePopup: togglePopupSelector(state),
      partTypeFilter: partTypeSelector(state),
      densityFilter: densitySelector(state),
      orgSizeFilter: orgSizeSelector(state),
      orgWidthFilter: orgWidthSelector(state),
      channelFilter: channelSelector(state),
      configFilter: configSelector(state),
      curConfig: curConfigSelector(state),
      isAddPart: isAddPartSelector(state),
      filterPairs: filterPairsSelector(state),
      approvedFilterPairs: approvedFilterPairsSelector(state),
      riskFilterPairs: riskFilterPairsSelector(state),
    }
  }

  const mapDispatchToProps = dispatch => {
    return {
      onClockChange: (index, value) => {
        dispatch(updateClockAction(index, value))
      },
      onTogglePopup: () => {
        dispatch(togglePopupAction())
      },
      onPartTypeChange: (index, value) => {
        dispatch(togglePartType(index, value))
      },
      onDensityChange: (index, value) => {
        dispatch(toggleDensity(index, value))
      },
      onOrgSizeChange: (index, value) => {
        dispatch(toggleOrgSize(index, value))
      },
      onOrgWidthChange: (index, value) => {
        dispatch(toggleOrgWidth(index, value))
      },
      onChannelChange: (index, value) => {
        dispatch(toggleChannel(index, value))
      },
      onConfigChange: (index, value) => {
        dispatch(toggleConfig(index, value))
      },
      onUpdateConfig: (value) => {
        dispatch(updateConfigAction(value))
      },
      onUpdateConfigSource: (value) => {
        dispatch(updateConfigSourceAction(value))
      },
      onUpdateCurConfig: (value) => {
        dispatch(updateCurConfigAction(value))
      },
      initConfig: () => {
        dispatch(initConfigAction())
      },
      onAddPart: (value) => {
        dispatch(addConfigAction(value))
      },
      onDeletePart: (value) => {
        dispatch(deletePartAction(value))
      },
      onRefreshFilter: () => {
        dispatch(refreshFilters())
      },
      setIsAddPart: (value) => {
        dispatch(isAddPart(value))
      },
      initConfigFilter: (filters) => {
        dispatch(initConfigFilter(filters))
      },
      initFilters: (filters, curConfig) => {
        dispatch(initFilters(filters, curConfig)())
      }
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)
}

const withRiskConnect = (path, id) => {
  const valuePath = getPath(path, id)
  const runRiskValidation = createNamedAction(valuePath, runRiskValidationAction)

  const mapStateToProps = () => ({})
  const mapDispatchToProps = (dispatch) => {
    return {
      runRiskValidation: () => dispatch(runRiskValidation())
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)
}

const withRisk = (path, id) => R.compose(
  withSagas(path, id),
  withRiskConnect(path, id),
  lifecycle({
    componentDidMount() {
      this.props.runRiskValidation()
    }
  })
)

export { default as Memory } from 'components/form/memory/index'
export {
  withConnect,
  withUiReducer,
  withStateReducer
}

const enhance = R.converge(
  R.compose,
  [
    withStateReducer,
    withUiReducer,
    withConnect,
    withRisk
  ]
)

export default R.curry((path, id) => enhance(path, id)(Memory))
